import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Dashboard from './components/Dashboard';
import DashboardPOS from './components/pos/DashboardPOS';
import MenuPublico from './components/pos/MenuPublico';
import BookingPublico from './components/pos/BookingPublico';
import API_URL from './config/api';
import LANDING_URL from './config/landing';

function App() {
  const [view, setView] = useState('loading');
  const [clientData, setClientData] = useState(null);
  const [token, setToken] = useState(null);

  // ⭐ Menú Web Estático (público): si la URL es /menu/:slug se sirve la
  // página del menú directo, SIN pasar por el flujo de sesión de abajo — un
  // comensal escaneando un QR en la mesa nunca debe necesitar una cuenta.
  // Se calcula en cada render (no en useEffect) para que ni siquiera se
  // vea el "Cargando..." del flujo de login antes de decidir qué mostrar.
  // v1 usa una ruta dentro del mismo dominio (/menu/lasazondeoso); el día
  // que haya subdominios reales (lasazondeoso.marketos.com) solo cambia
  // cómo se detecta el slug, no el resto de este componente.
  const publicMenuMatch = window.location.pathname.match(/^\/menu\/([a-z0-9-]+)\/?$/i);
  const publicMenuSlug = publicMenuMatch ? publicMenuMatch[1] : null;

  // ⭐ Calendarios (público): mismo patrón exacto que /menu/:slug de arriba
  // -- /reservar/:slug se sirve directo, sin sesión, para que cualquier
  // cliente con el link pueda reservar sin necesitar cuenta.
  const publicBookingMatch = window.location.pathname.match(/^\/reservar\/([a-z0-9-]+)\/?$/i);
  const publicBookingSlug = publicBookingMatch ? publicBookingMatch[1] : null;

  // ⭐ FIX CRÍTICO (2026-07-28): la identidad de sesión (token, userType,
  // clientId, employeeName/Role/Permissions, etc) se guardaba en
  // localStorage, que es COMPARTIDO por todas las pestañas del mismo
  // navegador. Esto causaba que, si alguien iniciaba sesión como dueño en
  // una segunda pestaña (o como otro empleado), esos datos sobreescribían
  // los de la PRIMERA pestaña -- y si esa primera pestaña se recargaba
  // (ej. Chrome descarta pestañas en segundo plano para ahorrar memoria),
  // "heredaba" silenciosamente la sesión de la otra pestaña, incluyendo el
  // acceso completo de dueño en lo que debía seguir siendo una sesión de
  // empleado restringida (ej. modo kiosko de Cocina). Se migra a
  // sessionStorage, que es independiente POR PESTAÑA: cada pestaña
  // mantiene su propia sesión sin importar qué se loguee en otra.
  useEffect(() => {
    if (publicMenuSlug || publicBookingSlug) return; // página pública: no toca sessionStorage ni redirige a la landing

    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    const userTypeFromUrl = urlParams.get('userType');

    if (tokenFromUrl && userTypeFromUrl) {
      sessionStorage.setItem('token', tokenFromUrl);
      sessionStorage.setItem('userType', userTypeFromUrl);

      if (userTypeFromUrl === 'admin') {
        const userNameFromUrl = urlParams.get('userName');
        if (userNameFromUrl) sessionStorage.setItem('userName', userNameFromUrl);
      }

      if (userTypeFromUrl === 'cliente' || userTypeFromUrl === 'employee') {
        const clientIdFromUrl = urlParams.get('clientId');
        const clientNameFromUrl = urlParams.get('clientName');
        const clientCategoryFromUrl = urlParams.get('clientCategory');
        const clientPlanFromUrl = urlParams.get('clientPlan');
        // ⭐ Cuenta CRM-only (registro/login standalone del producto CRM, ver
        // CRM.jsx en marketos-landing): 'crm' | 'pos' (default). DashboardPOS.js
        // usa esto para mostrar solo las secciones de CRM.
        const clientProductTypeFromUrl = urlParams.get('clientProductType');
        if (clientIdFromUrl) sessionStorage.setItem('clientId', clientIdFromUrl);
        if (clientNameFromUrl) sessionStorage.setItem('clientName', clientNameFromUrl);
        if (clientCategoryFromUrl) sessionStorage.setItem('clientCategory', clientCategoryFromUrl);
        if (clientPlanFromUrl) sessionStorage.setItem('clientPlan', clientPlanFromUrl);
        if (clientProductTypeFromUrl) sessionStorage.setItem('clientProductType', clientProductTypeFromUrl);

        if (userTypeFromUrl === 'employee') {
          const employeeNameFromUrl = urlParams.get('employeeName');
          const employeeRoleFromUrl = urlParams.get('employeeRole');
          const employeePermissionsFromUrl = urlParams.get('employeePermissions');
          if (employeeNameFromUrl) sessionStorage.setItem('employeeName', employeeNameFromUrl);
          if (employeeRoleFromUrl) sessionStorage.setItem('employeeRole', employeeRoleFromUrl);
          if (employeePermissionsFromUrl) sessionStorage.setItem('employeePermissions', employeePermissionsFromUrl);
        }
      }

      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const storedToken = sessionStorage.getItem('token');
    const userType = sessionStorage.getItem('userType');

    if (!storedToken) {
      window.location.replace(LANDING_URL);
      return;
    }

    setToken(storedToken);

    if (userType === 'admin') {
      setView('saas');
    } else if (userType === 'cliente' || userType === 'employee') {
      const clientPlan = sessionStorage.getItem('clientPlan') || 'basico';
      setClientData({
        id: sessionStorage.getItem('clientId'),
        name: userType === 'employee' ? sessionStorage.getItem('employeeName') : sessionStorage.getItem('clientName'),
        business_name: sessionStorage.getItem('clientName'),
        category: sessionStorage.getItem('clientCategory'),
        plan: clientPlan,
        productType: sessionStorage.getItem('clientProductType') || 'pos',
        crmPlanTier: sessionStorage.getItem('clientCrmPlanTier') || 'emprendedor',
        status: 'active',
        role: sessionStorage.getItem('employeeRole') || 'owner',
        permissions: sessionStorage.getItem('employeePermissions')
          ? JSON.parse(sessionStorage.getItem('employeePermissions'))
          : {}
      });
      setView('pos-dashboard');
    } else {
      window.location.replace(LANDING_URL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ⭐ Sincronización de sesión entre dispositivos (pedido de Angelo,
  // 2026-08-07): clientData arriba se arma UNA SOLA VEZ con lo que llegó en
  // los query params de la redirección de login, y de ahí queda cacheado en
  // sessionStorage por el resto de esa sesión de pestaña/dispositivo. Eso
  // causaba que un empleado viera permisos/plan desactualizados si el dueño
  // los cambiaba después de ese login, o si el login mismo no mandaba el
  // dato (bug real encontrado: el login de empleado nunca mandaba
  // clientPlan, así que Cocina -que exige plan Estándar+- quedaba oculta
  // para cualquier empleado sin importar el plan real de la cuenta).
  // Este efecto llama a GET /api/pos/session (fuente de verdad, siempre
  // fresca desde la BD, ver backend/index.js) apenas hay token, y pisa
  // clientData + sessionStorage con lo que devuelva -- así cada
  // dispositivo/pestaña se autocorrige en su próxima carga sin necesitar un
  // login nuevo. Deliberadamente silencioso si falla (offline, token
  // vencido, etc.): la app sigue funcionando con lo que ya tenía en
  // sessionStorage, esto es solo una corrección oportunista.
  useEffect(() => {
    if (view !== 'pos-dashboard' || !token) return;
    axios.get(`${API_URL}/api/pos/session`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const { client, employee } = res.data;
        if (!client) return;
        sessionStorage.setItem('clientCategory', client.category || '');
        sessionStorage.setItem('clientPlan', client.plan || 'basico');
        sessionStorage.setItem('clientProductType', client.product_type || 'pos');
        // ⭐ Mismo fix que plan/productType de arriba, pero para el plan REAL
        // de una cuenta CRM-only (pedido de Angelo, 2026-08-08): `plan` no
        // sirve para estas cuentas (siempre 'basico'), así que el sidebar
        // necesita `crm_plan_tier` para mostrar el plan correcto sin
        // requerir que el usuario cierre sesión y vuelva a entrar.
        sessionStorage.setItem('clientCrmPlanTier', client.crm_plan_tier || 'emprendedor');
        const patch = {
          category: client.category || '',
          plan: client.plan || 'basico',
          productType: client.product_type || 'pos',
          crmPlanTier: client.crm_plan_tier || 'emprendedor',
        };
        if (employee) {
          sessionStorage.setItem('employeeName', employee.name || '');
          sessionStorage.setItem('employeeRole', employee.role || 'employee');
          sessionStorage.setItem('employeePermissions', JSON.stringify(employee.permissions || {}));
          patch.name = employee.name;
          patch.role = employee.role || 'owner';
          patch.permissions = employee.permissions || {};
        }
        setClientData(prev => (prev ? { ...prev, ...patch } : prev));
      })
      .catch(() => {
        // silencioso a propósito -- ver comentario arriba
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, token]);

  // ⭐ FIX: deslizar "atrás" (swipe-back) en el navegador de un celular
  // navegaba al historial ANTERIOR a esta pantalla — casi siempre la URL de
  // redirección de un solo uso que llega desde la landing (?token=...&...),
  // que ya no es válida una vez consumida (o ya no existe como tal). Eso
  // rompía la app: pantalla en negro con error "not found". Esta pantalla no
  // tiene un "atrás" real (no usa rutas, la navegación interna es estado de
  // React, no URLs), así que se atrapa el gesto: en vez de dejar salir al
  // navegador del historial de la app, se vuelve a empujar el mismo estado
  // y el usuario se queda donde estaba.
  useEffect(() => {
    if (view === 'loading') return;
    window.history.pushState({ posApp: true }, '', window.location.href);
    const handlePopState = () => {
      window.history.pushState({ posApp: true }, '', window.location.href);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [view]);

  const handleLogout = () => {
    // Solo se limpia la sesión (sessionStorage); las preferencias del
    // dispositivo (color de fondo, timer de cocina, ancho de papel, etc,
    // guardadas en localStorage) sobreviven al cierre de sesión a propósito.
    sessionStorage.clear();
    window.location.replace(LANDING_URL);
  };

  if (publicMenuSlug) {
    return <MenuPublico slug={publicMenuSlug} />;
  }

  if (publicBookingSlug) {
    return <BookingPublico slug={publicBookingSlug} />;
  }

  if (view === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Cargando...</p>
        </div>
      </div>
    );
  }

  // Clientes y empleados van directo al POS sin barra superior
  if (view === 'pos-dashboard') {
    return (
      <DashboardPOS
        clientData={clientData}
        token={token}
        onLogout={handleLogout}
        onProfileUpdate={(updatedData) => {
          setClientData({ ...clientData, ...updatedData });
        }}
      />
    );
  }

  // Solo admin ve la barra de navegacion superior
  return (
    <div>
      <div className="bg-gray-800 text-white py-3 px-6 flex justify-between items-center shadow-lg">
        <div className="flex gap-4">
          <button
            onClick={() => setView('saas')}
            className={`px-4 py-2 rounded transition-all ${
              view === 'saas' ? 'bg-blue-600 shadow-lg' : 'bg-gray-700 hover:bg-gray-600'
            }`}
          >
            Gestor SaaS
          </button>
          <div className="flex items-center px-4 py-2 bg-gray-700 rounded">
            <span className="text-sm">
              {sessionStorage.getItem('userName') || 'Admin'}
            </span>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="bg-red-500 hover:bg-red-600 px-4 py-2 rounded transition-all flex items-center gap-2 shadow-lg"
        >
          Cerrar Sesion
        </button>
      </div>
      <div className="min-h-screen">
        {view === 'saas' && <Dashboard />}
      </div>
    </div>
  );
}

export default App;
